// screens/CalendarSpread.tsx — B17a: one event, prepared. The walked B10/B13
// machinery finally gets its surface: the coded assembly (party, engagement,
// threads, promises both directions, documents — each wearing HOW it got
// here) and the B13b async briefing note settling in above it.
//
// The note wears the interpreted label with its model named and its source
// count — reorder-never-remove is the SERVER's law; this screen renders the
// assembly exactly as answered. `gaps` render verbatim: "nothing to say" and
// "could not say" must never look identical (B10's rule).
import { useEffect, useRef, useState } from 'preact/hooks';
import { InterpretationLabel, SourceCitationChip } from '@vowric/ui';
import {
  ApiError,
  getEventBriefing,
  type EventBriefingApi,
  type PreparedItemApi,
} from '../lib/api';
import { BackLink } from '../components/BackLink';
import './CalendarSpread.css';

export interface CalendarSpreadProps {
  tenantId: string;
  id: string;
  backLabel?: string;
  onBack: () => void;
  onOpenThread: (id: string) => void;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; briefing: EventBriefingApi };

const POLL_MS = 3_000;
const POLL_MAX = 40;

function whenLabel(event: Record<string, unknown>): string {
  const startLocal = event['start_local'] as string | null;
  const tz = event['timezone'] as string | null;
  const startsAt = event['starts_at'] as string;
  if (startLocal) {
    const day = new Date(startsAt).toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
    return `${day} · ${startLocal.slice(11, 16)}${tz ? ` ${tz.split('/').pop()?.replace('_', ' ')}` : ''}`;
  }
  return new Date(startsAt).toLocaleString([], { weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function itemName(i: PreparedItemApi): string {
  return (i['subject'] as string) ?? (i['title'] as string) ?? (i['display_name'] as string)
    ?? (i['name'] as string) ?? (i['current_title'] as string) ?? (i['original_filename'] as string) ?? '(unnamed)';
}

function Via({ i }: { i: PreparedItemApi }) {
  // Explicit outranks derived, and the mark says so: a person deliberately
  // attached the explicit ones.
  return (
    <span class="cspread__via">
      {i.source === 'explicit' ? 'attached' : i.via ?? 'derived'}
    </span>
  );
}

export function CalendarSpread({ tenantId, id, backLabel, onBack, onOpenThread }: CalendarSpreadProps) {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });
  // The shimmer is a promise of an answer; when the polling budget runs out
  // the promise is withdrawn IN WORDS — an eternal shimmer is a quiet lie.
  const [noteGaveUp, setNoteGaveUp] = useState(false);
  const polls = useRef(0);

  useEffect(() => {
    let cancelled = false;
    setLoad({ phase: 'loading' });
    setNoteGaveUp(false);
    polls.current = 0;
    const poll = () => {
      if (cancelled) return;
      getEventBriefing(tenantId, id)
        .then((briefing) => {
          if (cancelled) return;
          setLoad({ phase: 'ready', briefing });
          if (briefing.noteStatus === 'composing') {
            polls.current += 1;
            if (polls.current < POLL_MAX) setTimeout(poll, POLL_MS);
            else setNoteGaveUp(true);
          }
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          if (err instanceof ApiError && err.status === 404) {
            setLoad({ phase: 'error', message: 'event not found' });
          } else {
            setLoad({
              phase: 'error',
              message: err instanceof ApiError ? err.message : 'the briefing could not be read',
            });
          }
        });
    };
    poll();
    return () => { cancelled = true; };
  }, [tenantId, id]);

  if (load.phase === 'loading') {
    return (
      <div class="cspread">
        <BackLink label={backLabel ?? 'Calendar'} onClick={onBack} />
        <div class="cspread__skeleton" aria-hidden="true">
          <div class="cspread__skeleton-block" />
          <div class="cspread__skeleton-block" />
        </div>
      </div>
    );
  }

  if (load.phase === 'error') {
    return (
      <div class="cspread">
        <BackLink label={backLabel ?? 'Calendar'} onClick={onBack} />
        <div class="cspread__say">
          This event could not be read.
          <br />
          <span class="cspread__say-soft">{load.message}</span>
        </div>
      </div>
    );
  }

  const b = load.briefing;
  const event = b.preparation.event;
  const cancelled = (event['status'] as string) === 'cancelled';
  const groups: Array<{ title: string; items: PreparedItemApi[]; onTap?: (i: PreparedItemApi) => void }> = [
    { title: 'Conversations', items: b.preparation.threads, onTap: (i) => onOpenThread(i['id'] as string) },
    { title: 'You owe', items: b.preparation.commitments.owedByMe },
    { title: 'Owed to you', items: b.preparation.commitments.owedToMe },
    { title: 'Documents', items: b.preparation.documents },
  ];

  return (
    <div class="cspread">
      <BackLink label={backLabel ?? 'Calendar'} onClick={onBack} />

      <header class="cspread__head">
        <div class="cspread__when">
          {cancelled ? <span class="cspread__cancelled-word">cancelled · </span> : null}
          {whenLabel(event)}
        </div>
        <h1 class="cspread__title">{(event['title'] as string) ?? '(untitled)'}</h1>
        {(event['location'] as string) ? <div class="cspread__where">{event['location'] as string}</div> : null}
        {(event['organizer'] as string) ? <div class="cspread__who">{event['organizer'] as string}</div> : null}
      </header>

      {/* The note: composing shimmer -> the interpreted paragraph, cited. */}
      {b.noteStatus === 'composing' ? (
        noteGaveUp ? (
          <div class="cspread__gap">The note didn't arrive — it may come later.</div>
        ) : (
          <div class="cspread__note cspread__note--composing" aria-hidden="true">
            <div class="cspread__note-shimmer" />
            <div class="cspread__note-shimmer cspread__note-shimmer--short" />
          </div>
        )
      ) : b.note ? (
        <div class="cspread__note">
          <InterpretationLabel kind="interpreted">{b.note.text}</InterpretationLabel>
          <div class="cspread__note-cite">
            <SourceCitationChip
              source={`briefing · ${b.note.model}`}
              when={`${b.note.sources.length} sources${b.aiOrdered ? ' · ordered' : ''}`}
            />
          </div>
        </div>
      ) : null}

      {b.preparation.gaps.length > 0 ? (
        <div class="cspread__gaps">
          {b.preparation.gaps.map((g) => (
            <div class="cspread__gap" key={g}>{g}</div>
          ))}
        </div>
      ) : null}

      {b.preparation.party ? (
        <section class="cspread__group">
          <h2 class="cspread__group-title">Who</h2>
          <div class="cspread__item">
            <span class="cspread__item-name">{itemName(b.preparation.party)}</span>
            <Via i={b.preparation.party} />
          </div>
        </section>
      ) : null}

      {groups.map((g) =>
        g.items.length > 0 ? (
          <section class="cspread__group" key={g.title}>
            <h2 class="cspread__group-title">{g.title}</h2>
            {g.items.map((i) =>
              g.onTap ? (
                <button type="button" class="cspread__item cspread__item--tap" key={String(i['id'])} onClick={() => g.onTap!(i)}>
                  <span class="cspread__item-name">{itemName(i)}</span>
                  <Via i={i} />
                </button>
              ) : (
                <div class="cspread__item" key={String(i['id'])}>
                  <span class="cspread__item-name">{itemName(i)}</span>
                  <Via i={i} />
                </div>
              )
            )}
          </section>
        ) : null
      )}
    </div>
  );
}
